import type { SchemaElement } from "../../../api/restaurantSchema";
import type { StageLayout } from "../../../components/restaurant-schema/SchemaStage";
import type { TableLinkGroup, TableLinkTone } from "../logic/tableLinks";

type LinkPattern = "solid" | "dash" | "dot";

/**
 * Как изглежда връзката по вид. Цветовете са токените на сцената (светла +
 * тъмна стойност в index.css). Избраната е силна — широка четка и пунктир в
 * оранжевото на ореола ѝ. Постоянните (при всяко положение на линията) са
 * спокойни — тънки, с бледа четка, за да не спорят с надписите на масите — и
 * повтарят контура на плота от легендата: плътна за „Заета", пунктир за
 * „Резервирана", точки в цвета на чипа за следващата резервация.
 */
const LINK_LOOK: Record<
  TableLinkTone,
  { strokeClass: string; strong: boolean; brushOpacity: number; coreOpacity: number; pattern: LinkPattern }
> = {
  selected: { strokeClass: "stroke-brand", strong: true, brushOpacity: 0.18, coreOpacity: 0.85, pattern: "dash" },
  occupied: {
    strokeClass: "stroke-schema-occupied-edge",
    strong: false,
    brushOpacity: 0.08,
    coreOpacity: 0.5,
    pattern: "solid",
  },
  reserved: {
    strokeClass: "stroke-schema-reserved-edge",
    strong: false,
    brushOpacity: 0.08,
    coreOpacity: 0.55,
    pattern: "dash",
  },
  // Цветът на чипа е по-светъл — линията е почти плътна, иначе точките се губят в пода.
  upcoming: { strokeClass: "stroke-schema-next-edge", strong: false, brushOpacity: 0.12, coreOpacity: 0.95, pattern: "dot" },
};

/** Пунктирът на средната линия по дебелината ѝ (точките са кръгли краища с нулева дължина). */
const dashArrayOf = (pattern: LinkPattern, coreWidth: number) => {
  if (pattern === "dash") return `${coreWidth * 2.5} ${coreWidth * 2.5}`;
  if (pattern === "dot") return `0 ${coreWidth * 2}`;
  return undefined;
};

/**
 * Подрежда масите във верига: от най-лявата към най-близката следваща —
 * при 3+ маси линията не се кръстосва сама.
 */
const chainOf = (points: { x: number; y: number }[]) => {
  const rest = [...points].sort((left, right) => left.x - right.x || left.y - right.y);
  const chain = rest.splice(0, 1);
  while (rest.length > 0) {
    const last = chain[chain.length - 1];
    let nearest = 0;
    rest.forEach((point, index) => {
      const best = rest[nearest];
      if (Math.hypot(point.x - last.x, point.y - last.y) < Math.hypot(best.x - last.x, best.y - last.y)) {
        nearest = index;
      }
    });
    chain.push(...rest.splice(nearest, 1));
  }
  return chain;
};

/**
 * „Четка" между масите на една резервация — под масите, над пода: мек широк
 * щрих в цвета на статуса + тънка линия по средата. Показва, че 1 и 2 са една
 * обща резервация — и когато само на едната има сметка, и когато над трите
 * стои един и същ час „18:30". Групите идват готови (`tableLinkGroupsOf`) —
 * всяка резервация е отделна верига, избраната се рисува последна (отгоре).
 */
export const TableLinks = ({
  layout,
  groups,
}: {
  layout: StageLayout;
  groups: TableLinkGroup<SchemaElement>[];
}) => {
  if (groups.length === 0) return null;

  return (
    <svg
      className="pointer-events-none absolute inset-0 overflow-visible"
      width={layout.width}
      height={layout.height}
      aria-hidden
    >
      {groups.map((group) => {
        const look = LINK_LOOK[group.tone];
        // Дебелините следват мащаба (зуум), с долна граница — да се виждат и при отдалечаване.
        const brushWidth = look.strong
          ? Math.min(26, Math.max(10, 20 * layout.scale))
          : Math.min(18, Math.max(8, 14 * layout.scale));
        const coreWidth = look.strong
          ? Math.max(2, 3 * layout.scale)
          : look.pattern === "dot"
            ? Math.max(2.5, 3.5 * layout.scale)
            : Math.max(1.5, 2 * layout.scale);
        const chain = chainOf(
          group.tables.map((table) => {
            const box = layout.boxOf(table);
            return { x: box.centerX, y: box.centerY };
          }),
        );
        const path = chain.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" ");
        return (
          <g key={group.key} className={look.strokeClass} fill="none" strokeLinecap="round" strokeLinejoin="round">
            <path d={path} strokeWidth={brushWidth} strokeOpacity={look.brushOpacity} />
            <path
              d={path}
              strokeWidth={coreWidth}
              strokeOpacity={look.coreOpacity}
              strokeDasharray={dashArrayOf(look.pattern, coreWidth)}
            />
          </g>
        );
      })}
    </svg>
  );
};
